import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Sparkles, ChevronRight, ChevronLeft, RotateCcw, Megaphone, MousePointerClick,
  Users, Target, ShoppingBag, Smartphone, TrendingUp, Zap, Play, ExternalLink,
  ChevronDown, ChevronUp, ImageIcon, Layers,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Textarea } from '@/components/Textarea';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { Card } from '@/components/Card';
import {
  aiCampaignService,
  GenerateCampaignPayload,
  GenerateCampaignResponse,
  CampaignObjective,
  WorkspaceDraft,
} from '@/services/ai-campaign.service';
import { settingsService, AiModelsResponse, AiProviderName } from '@/services/settings.service';
import { facebookAdAccountService, MetaAdAccount } from '@/services/facebook-ad-account.service';
import { AccountHealthBanner } from '@/components/facebook/AccountHealthBanner';
import { CountryMultiPicker } from '@/components/CountryMultiPicker';
import { FbWorkspaceProvider, useFbWorkspace } from '@/contexts/FbWorkspaceContext';

// ─── Constants ────────────────────────────────────────────────────────────────

const OBJECTIVES: { value: CampaignObjective; label: string; desc: string; icon: React.ReactNode }[] = [
  { value: 'OUTCOME_AWARENESS',     label: 'Awareness',       desc: 'Reach & brand visibility',     icon: <TrendingUp size={20} /> },
  { value: 'OUTCOME_TRAFFIC',       label: 'Traffic',         desc: 'Clicks & landing page views',  icon: <MousePointerClick size={20} /> },
  { value: 'OUTCOME_ENGAGEMENT',    label: 'Engagement',      desc: 'Likes, comments, messages',    icon: <Users size={20} /> },
  { value: 'OUTCOME_LEADS',         label: 'Leads',           desc: 'Lead forms & messages',        icon: <Target size={20} /> },
  { value: 'OUTCOME_SALES',         label: 'Sales',           desc: 'Conversions & catalog sales',  icon: <ShoppingBag size={20} /> },
  { value: 'OUTCOME_APP_PROMOTION', label: 'App Promotion',   desc: 'App installs & events',        icon: <Smartphone size={20} /> },
];

const PROMPT_CHIPS = [
  'Promote my Black Friday sale on home appliances to UAE residents 25-45 with $50/day',
  'Get more WhatsApp leads for my dental clinic in Dubai',
  'Drive app installs in UAE and Saudi Arabia for a food delivery app',
  'Increase brand awareness for my fashion brand targeting women 20-35',
  'Generate leads for a real estate project in Abu Dhabi',
  'Boost engagement on our new product launch post',
];

const LOADER_PHRASES = [
  'Analyzing your brief...',
  'Drafting campaign copy...',
  'Picking optimization goals...',
  'Selecting audience parameters...',
  'Finalizing budget structure...',
  'Almost there...',
];

const STEPS = ['Goal', 'Objective & Budget', 'Audience', 'Creative & Shape'];

// ─── Segmented control ────────────────────────────────────────────────────────

function Segmented<T extends string>({
  options, value, onChange,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded-lg border border-border bg-surface-alt p-0.5 gap-0.5">
      {options.map(o => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={clsx(
            'rounded-md px-3 py-1.5 text-xs font-medium transition-all',
            o.value === value
              ? 'bg-surface text-text-primary shadow-soft'
              : 'text-text-muted hover:text-text-primary',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ─── Step progress header ─────────────────────────────────────────────────────

function StepProgress({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-2 px-1">
      {STEPS.map((label, idx) => (
        <React.Fragment key={label}>
          <div className="flex items-center gap-2">
            <div className={clsx(
              'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors',
              idx < current ? 'bg-primary text-on-primary' :
              idx === current ? 'bg-primary-soft border-2 border-primary text-primary' :
              'bg-surface-alt text-text-muted border border-border',
            )}>
              {idx < current ? '✓' : idx + 1}
            </div>
            <span className={clsx(
              'text-xs font-medium hidden sm:block',
              idx === current ? 'text-text-primary' : 'text-text-muted',
            )}>
              {label}
            </span>
          </div>
          {idx < STEPS.length - 1 && (
            <div className={clsx(
              'h-px flex-1 transition-colors',
              idx < current ? 'bg-primary' : 'bg-border',
            )} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Loader overlay ───────────────────────────────────────────────────────────

function GeneratingLoader({ phrase }: { phrase: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-6 py-20">
      <div className="relative flex h-20 w-20 items-center justify-center">
        <div className="absolute inset-0 rounded-full border-4 border-primary/20" />
        <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary animate-spin" />
        <Sparkles size={28} className="text-primary animate-pulse" />
      </div>
      <div className="space-y-1 text-center">
        <p className="text-sm font-semibold text-text-primary">Generating your campaign...</p>
        <p className="text-xs text-text-muted animate-pulse">{phrase}</p>
      </div>
    </div>
  );
}

// ─── Result view ──────────────────────────────────────────────────────────────

function ResultView({
  result,
  onEditInWorkspace,
  onPublish,
  onRetry,
  publishing,
  currency,
}: {
  result: GenerateCampaignResponse;
  onEditInWorkspace: () => void;
  onPublish: () => void;
  onRetry: () => void;
  publishing: boolean;
  currency: string;
}) {
  const [reasoningOpen, setReasoningOpen] = useState(false);
  const { draft, reasoning } = result;
  const { campaign, adsets } = draft;

  const budgetLine = (() => {
    if (campaign.daily_budget) return `${currency} ${campaign.daily_budget}/day`;
    if (campaign.lifetime_budget) return `${currency} ${campaign.lifetime_budget} lifetime`;
    return 'Budget set per ad set';
  })();

  const objectiveMeta = OBJECTIVES.find(o => o.value === campaign.objective);

  return (
    <div className="space-y-5">
      {/* Campaign card */}
      <Card className="p-5">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary">
            {objectiveMeta?.icon ?? <Megaphone size={20} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-text-primary text-base">{campaign.name}</h3>
              <span className="rounded-full bg-warning-soft px-2 py-0.5 text-2xs font-semibold text-warning uppercase tracking-wide">
                PAUSED
              </span>
              <span className="rounded-full bg-primary-soft px-2 py-0.5 text-2xs font-medium text-primary">
                {objectiveMeta?.label ?? campaign.objective}
              </span>
            </div>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-muted">
              <span>{budgetLine}</span>
              <span>Budget: {campaign.budget_level === 'campaign' ? 'Campaign level (CBO)' : 'Ad Set level (ABO)'}</span>
              {campaign.start_time && <span>Starts {new Date(campaign.start_time).toLocaleDateString()}</span>}
            </div>
          </div>
        </div>
      </Card>

      {/* Ad sets */}
      {adsets.map((adset, i) => {
        const countries = adset.targeting?.geo_locations?.countries?.join(', ') ?? '—';
        const ages = adset.targeting?.age_min !== undefined
          ? `${adset.targeting.age_min}–${adset.targeting.age_max ?? 65}`
          : null;
        const interests = adset.targeting?.interests?.map(x => x.name).join(', ');
        const adsetBudget = adset.daily_budget
          ? `${currency} ${adset.daily_budget}/day`
          : adset.lifetime_budget
          ? `${currency} ${adset.lifetime_budget} lifetime`
          : null;

        return (
          <div key={i} className="rounded-xl border border-border bg-surface overflow-hidden">
            {/* Ad set header */}
            <div className="px-5 py-4 bg-info-soft/40 border-b border-border flex items-center gap-3">
              <Layers size={14} className="text-info shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-text-primary">{adset.name}</p>
                <p className="text-xs text-text-muted mt-0.5">
                  {adset.optimization_goal.replace(/_/g, ' ')}
                  {adsetBudget ? ` · ${adsetBudget}` : ''}
                </p>
              </div>
            </div>

            {/* Targeting summary */}
            <div className="px-5 py-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-text-muted border-b border-border">
              <span><span className="font-medium text-text-secondary">Countries:</span> {countries}</span>
              {ages && <span><span className="font-medium text-text-secondary">Ages:</span> {ages}</span>}
              {interests && <span><span className="font-medium text-text-secondary">Interests:</span> {interests}</span>}
            </div>

            {/* Ads */}
            <div className="divide-y divide-border">
              {adset.ads.map((ad, j) => (
                <div key={j} className="px-5 py-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface-alt text-text-muted">
                      <ImageIcon size={14} />
                    </div>
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-text-primary">{ad.name}</p>
                        <span className="rounded bg-surface-alt px-1.5 py-0.5 text-2xs font-medium text-text-muted uppercase">
                          {ad.creative.media_kind}
                        </span>
                        {ad.creative.creative_pending && (
                          <span className="rounded bg-warning-soft px-1.5 py-0.5 text-2xs font-semibold text-warning">
                            Media needed
                          </span>
                        )}
                      </div>
                      {ad.creative.message && (
                        <p className="text-xs text-text-secondary line-clamp-2">{ad.creative.message}</p>
                      )}
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-text-muted">
                        {ad.creative.headline && <span><span className="font-medium">Headline:</span> {ad.creative.headline}</span>}
                        {ad.creative.cta_type && <span><span className="font-medium">CTA:</span> {ad.creative.cta_type.replace(/_/g, ' ')}</span>}
                        {ad.creative.destination_url && (
                          <a
                            href={ad.creative.destination_url}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-1 text-primary hover:underline"
                          >
                            <ExternalLink size={10} /> Link
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* AI reasoning */}
      {reasoning && (
        <div className="rounded-xl border border-border overflow-hidden">
          <button
            type="button"
            onClick={() => setReasoningOpen(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 bg-surface hover:bg-surface-alt transition-colors text-sm"
          >
            <span className="flex items-center gap-2 font-medium text-text-secondary">
              <Sparkles size={13} className="text-primary" />
              Why AI chose this structure
            </span>
            {reasoningOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {reasoningOpen && (
            <div className="px-4 py-3 bg-surface-alt/40 border-t border-border">
              <p className="text-xs text-text-secondary leading-relaxed">{reasoning}</p>
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-3 pt-2">
        <Button
          variant="primary"
          onClick={onEditInWorkspace}
          iconLeft={<Zap size={14} />}
        >
          Edit in Workspace
        </Button>
        <Button
          variant="secondary"
          onClick={onPublish}
          loading={publishing}
          disabled={publishing}
          iconLeft={<Play size={14} />}
        >
          Publish to Meta as PAUSED
        </Button>
        <Button
          variant="ghost"
          onClick={onRetry}
          iconLeft={<RotateCcw size={14} />}
        >
          Try Again
        </Button>
      </div>

      <p className="text-xs text-text-muted">
        "Publish to Meta as PAUSED" creates everything on Meta immediately. You can then review and click Activate to go live.
      </p>
    </div>
  );
}

// ─── Inner wizard (needs workspace context for applyDraft) ────────────────────

function WizardInner() {
  const navigate = useNavigate();
  const { applyDraft } = useFbWorkspace();

  // Wizard state
  const [step, setStep] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [generatingPhrase, setGeneratingPhrase] = useState(LOADER_PHRASES[0]);
  const [publishing, setPublishing] = useState(false);
  const [result, setResult] = useState<GenerateCampaignResponse | null>(null);
  const phraseIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // AI model state
  const [modelsData, setModelsData] = useState<AiModelsResponse | null>(null);
  const [aiProvider, setAiProvider] = useState<AiProviderName>('openai');
  const [aiModel, setAiModel] = useState<string>('gpt-4o-mini');

  // Ad account context
  // Filled from the ad account below; empty until Meta tells us.
  const [currency, setCurrency] = useState('');
  const [account, setAccount] = useState<MetaAdAccount | null>(null);

  // Step 1 — Goal
  const [brief, setBrief] = useState('');

  // Step 2 — Objective & Budget
  const [objective, setObjective] = useState<CampaignObjective | ''>('');
  const [budgetAt, setBudgetAt] = useState<'campaign' | 'adset'>('campaign');
  const [budgetType, setBudgetType] = useState<'daily' | 'lifetime'>('daily');
  const [budgetAmount, setBudgetAmount] = useState('');

  // Step 3 — Audience
  const [countries, setCountries] = useState<string[]>([]);
  const [ageMin, setAgeMin] = useState('18');
  const [ageMax, setAgeMax] = useState('65');
  const [gender, setGender] = useState<'all' | 'men' | 'women'>('all');
  const [interestsQuery, setInterestsQuery] = useState('');

  // Step 4 — Creative & Shape
  const [numAdsets, setNumAdsets] = useState('1');
  const [numAdsPerAdset, setNumAdsPerAdset] = useState('2');
  const [ctaType, setCtaType] = useState('');
  const [destinationUrl, setDestinationUrl] = useState('');
  const [includeInstagram, setIncludeInstagram] = useState(true);

  // Load models + ad account
  useEffect(() => {
    settingsService.listAiModels().then(d => {
      setModelsData(d);
      setAiProvider(d.default.provider);
      setAiModel(d.default.model);
    }).catch(() => {});
    facebookAdAccountService.get().then(acc => {
      setAccount(acc);
      if (acc?.currency) setCurrency(acc.currency);
    }).catch(() => {});
  }, []);

  // Rotating phrases during generation
  const startPhrases = () => {
    let idx = 0;
    setGeneratingPhrase(LOADER_PHRASES[0]);
    phraseIntervalRef.current = setInterval(() => {
      idx = (idx + 1) % LOADER_PHRASES.length;
      setGeneratingPhrase(LOADER_PHRASES[idx]);
    }, 1800);
  };

  const stopPhrases = () => {
    if (phraseIntervalRef.current) clearInterval(phraseIntervalRef.current);
  };

  useEffect(() => () => stopPhrases(), []);

  const genderNumbers = gender === 'men' ? [1] : gender === 'women' ? [2] : [];

  const buildPayload = (): GenerateCampaignPayload => {
    const payload: GenerateCampaignPayload = { brief };
    if (objective) payload.objective = objective as CampaignObjective;
    payload.budget_at = budgetAt;
    if (budgetAmount && !isNaN(parseFloat(budgetAmount))) {
      if (budgetType === 'daily') payload.daily_budget_amount = parseFloat(budgetAmount);
      else payload.lifetime_budget_amount = parseFloat(budgetAmount);
    }
    if (countries.length) payload.countries = countries;
    if (ageMin || ageMax) payload.age_range = { min: parseInt(ageMin) || 18, max: parseInt(ageMax) || 65 };
    if (genderNumbers.length) payload.genders = genderNumbers;
    if (interestsQuery.trim()) payload.interests_query = interestsQuery.trim();
    if (ctaType) payload.cta_type = ctaType;
    if (destinationUrl.trim()) payload.destination_url = destinationUrl.trim();
    const ns = parseInt(numAdsets);
    if (!isNaN(ns) && ns > 0) payload.num_adsets = ns;
    const na = parseInt(numAdsPerAdset);
    if (!isNaN(na) && na > 0) payload.num_ads_per_adset = na;
    payload.ai_provider = aiProvider;
    payload.ai_model = aiModel;
    return payload;
  };

  const handleGenerate = async () => {
    if (!brief.trim()) return;
    setGenerating(true);
    startPhrases();
    try {
      const r = await aiCampaignService.generate(buildPayload());
      setResult(r);
      toast.success(`Campaign generated · ${r.creditsCharged ?? 0} credits`);
    } catch (e: any) {
      // toast already shown by axios interceptor
    } finally {
      stopPhrases();
      setGenerating(false);
    }
  };

  const handleEditInWorkspace = () => {
    if (!result) return;
    applyDraft(result.draft);
    navigate('/facebook/workspace');
  };

  const handlePublish = async () => {
    if (!result) return;
    setPublishing(true);
    try {
      const r = await aiCampaignService.publishDraft(result.draft);
      toast.success('Campaign published as PAUSED on Meta!');
      // Apply draft to workspace and navigate there
      applyDraft(result.draft);
      navigate(`/facebook/campaigns/${r.campaign_id}`);
    } catch (e: any) {
      // toast shown by interceptor
    } finally {
      setPublishing(false);
    }
  };

  const handleRetry = () => {
    setResult(null);
    setStep(0);
  };

  const canGoNext = step === 0 ? brief.trim().length >= 5 : true;

  const availableProviders = modelsData?.providers.filter(p => p.available) ?? [];

  if (generating) {
    return (
      <div className="mx-auto w-full max-w-[800px] px-7 py-7">
        <GeneratingLoader phrase={generatingPhrase} />
      </div>
    );
  }

  if (result) {
    return (
      <div className="px-7 py-7 w-full">
        <h2 className="text-lg font-bold text-text-primary mb-4 flex items-center gap-2">
          <Sparkles size={18} className="text-primary" />
          Your AI-Generated Campaign
        </h2>
        <ResultView
          result={result}
          onEditInWorkspace={handleEditInWorkspace}
          onPublish={handlePublish}
          onRetry={handleRetry}
          publishing={publishing}
          currency={currency}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[800px] px-7 py-7 space-y-6">
      <AccountHealthBanner account={account} />
      {/* Step progress */}
      <StepProgress current={step} />

      {/* Step content */}
      {step === 0 && (
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-bold text-text-primary">Tell me about your campaign</h2>
            <p className="text-sm text-text-muted mt-1">
              Describe your goal, product, audience, and budget. The more detail you add, the better the result.
            </p>
          </div>

          <FormField label="Campaign brief" required>
            <Textarea
              value={brief}
              onChange={e => setBrief(e.target.value)}
              rows={5}
              placeholder="e.g. Promote my Black Friday sale on home appliances to UAE residents aged 25-45 with a $50/day budget, targeting people interested in home improvement"
            />
          </FormField>

          <div className="space-y-2">
            <p className="text-xs font-medium text-text-muted uppercase tracking-wide">Quick prompts</p>
            <div className="flex flex-wrap gap-2">
              {PROMPT_CHIPS.map(chip => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => setBrief(chip)}
                  className="rounded-full border border-border bg-surface px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-alt hover:text-text-primary transition-colors text-left"
                >
                  {chip.length > 55 ? chip.slice(0, 55) + '…' : chip}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-5">
          <div>
            <h2 className="text-xl font-bold text-text-primary">Objective & Budget</h2>
            <p className="text-sm text-text-muted mt-1">
              AI will pick the best objective from your brief, but you can override it here.
            </p>
          </div>

          {/* Objective tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {OBJECTIVES.map(obj => (
              <button
                key={obj.value}
                type="button"
                onClick={() => setObjective(obj.value === objective ? '' : obj.value)}
                className={clsx(
                  'flex flex-col items-start gap-1.5 rounded-xl border p-3 text-left transition-all',
                  objective === obj.value
                    ? 'border-primary/50 bg-primary-soft ring-1 ring-primary/20'
                    : 'border-border bg-surface hover:bg-surface-alt',
                )}
              >
                <span className={clsx(
                  'flex h-8 w-8 items-center justify-center rounded-lg',
                  objective === obj.value ? 'bg-primary text-on-primary' : 'bg-surface-alt text-text-muted',
                )}>
                  {obj.icon}
                </span>
                <span className="text-xs font-semibold text-text-primary">{obj.label}</span>
                <span className="text-2xs text-text-muted leading-tight">{obj.desc}</span>
              </button>
            ))}
          </div>
          {!objective && <p className="text-xs text-text-muted italic">No override — AI will choose from your brief.</p>}

          {/* Budget level */}
          <FormField label="Budget level">
            <Segmented
              options={[
                { value: 'campaign', label: 'Campaign (CBO)' },
                { value: 'adset', label: 'Ad Set (ABO)' },
              ]}
              value={budgetAt}
              onChange={setBudgetAt}
            />
            <p className="text-2xs text-text-muted mt-1.5">
              {budgetAt === 'campaign'
                ? 'Facebook distributes budget across ad sets automatically.'
                : 'Each ad set gets its own budget.'}
            </p>
          </FormField>

          {/* Budget type + amount */}
          <div className="flex gap-3 items-end">
            <div className="w-40 shrink-0">
              <FormField label="Budget type">
                <Select value={budgetType} onChange={e => setBudgetType(e.target.value as 'daily' | 'lifetime')}>
                  <option value="daily">Daily</option>
                  <option value="lifetime">Lifetime</option>
                </Select>
              </FormField>
            </div>
            <div className="flex-1">
              <FormField label={`Amount (${currency})`}>
                <Input
                  type="number"
                  min="1"
                  step="0.01"
                  value={budgetAmount}
                  onChange={e => setBudgetAmount(e.target.value)}
                  placeholder="50"
                />
              </FormField>
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5">
          <div>
            <h2 className="text-xl font-bold text-text-primary">Audience</h2>
            <p className="text-sm text-text-muted mt-1">
              AI will detect countries and interests from your brief. Fine-tune here.
            </p>
          </div>

          <FormField label="Target countries" hint="Pick from the list — search by name or ISO code.">
            <CountryMultiPicker
              value={countries}
              onChange={setCountries}
              placeholder="Pick countries (UAE, Saudi Arabia…)"
            />
          </FormField>

          <div className="flex gap-3">
            <div className="flex-1">
              <FormField label="Min age">
                <Input
                  type="number"
                  min="13"
                  max="64"
                  value={ageMin}
                  onChange={e => setAgeMin(e.target.value)}
                />
              </FormField>
            </div>
            <div className="flex-1">
              <FormField label="Max age">
                <Input
                  type="number"
                  min="14"
                  max="65"
                  value={ageMax}
                  onChange={e => setAgeMax(e.target.value)}
                />
              </FormField>
            </div>
          </div>

          <FormField label="Gender">
            <Segmented
              options={[
                { value: 'all', label: 'All' },
                { value: 'men', label: 'Men' },
                { value: 'women', label: 'Women' },
              ]}
              value={gender}
              onChange={setGender}
            />
          </FormField>

          <FormField label="Interests / behaviors" hint="Free-form — AI will pick specific Meta interest IDs">
            <Input
              value={interestsQuery}
              onChange={e => setInterestsQuery(e.target.value)}
              placeholder="e.g. home improvement, DIY, interior design"
            />
          </FormField>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-5">
          <div>
            <h2 className="text-xl font-bold text-text-primary">Creative & Shape</h2>
            <p className="text-sm text-text-muted mt-1">
              AI will write copy suggestions. You'll upload actual images/videos in the workspace.
            </p>
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <FormField label="Number of ad sets">
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={numAdsets}
                  onChange={e => setNumAdsets(e.target.value)}
                />
              </FormField>
            </div>
            <div className="flex-1">
              <FormField label="Ads per ad set">
                <Input
                  type="number"
                  min="1"
                  max="6"
                  value={numAdsPerAdset}
                  onChange={e => setNumAdsPerAdset(e.target.value)}
                />
              </FormField>
            </div>
          </div>

          <FormField label="Call to action (optional)">
            <Select value={ctaType} onChange={e => setCtaType(e.target.value)}>
              <option value="">AI chooses</option>
              <option value="LEARN_MORE">Learn More</option>
              <option value="SHOP_NOW">Shop Now</option>
              <option value="SIGN_UP">Sign Up</option>
              <option value="GET_QUOTE">Get Quote</option>
              <option value="CONTACT_US">Contact Us</option>
              <option value="BOOK_TRAVEL">Book Travel</option>
              <option value="DOWNLOAD">Download</option>
              <option value="INSTALL_MOBILE_APP">Install App</option>
              <option value="WHATSAPP_MESSAGE">WhatsApp</option>
              <option value="CALL_NOW">Call Now</option>
              <option value="ORDER_NOW">Order Now</option>
              <option value="APPLY_NOW">Apply Now</option>
              <option value="SUBSCRIBE">Subscribe</option>
            </Select>
          </FormField>

          <FormField label="Destination URL (optional)">
            <Input
              value={destinationUrl}
              onChange={e => setDestinationUrl(e.target.value)}
              placeholder="https://example.com/landing-page"
            />
          </FormField>

          <FormField label="Instagram">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeInstagram}
                onChange={e => setIncludeInstagram(e.target.checked)}
                className="rounded border-border"
              />
              <span className="text-sm text-text-secondary">Run on Instagram simultaneously</span>
            </label>
          </FormField>

          {/* AI model picker */}
          <FormField label="AI model">
            {/* Models come from the central registry — never hardcoded here. */}
            <Select
              value={`${aiProvider}::${aiModel}`}
              disabled={availableProviders.length === 0}
              onChange={e => {
                const [p, m] = e.target.value.split('::') as [AiProviderName, string];
                setAiProvider(p);
                setAiModel(m);
              }}
            >
              {availableProviders.length === 0 && (
                <option value="">Loading models…</option>
              )}
              {availableProviders.map(entry => (
                <optgroup key={entry.id} label={entry.label}>
                  {entry.models.map(m => (
                    <option key={m.id} value={`${entry.id}::${m.id}`}>{m.label}</option>
                  ))}
                </optgroup>
              ))}
            </Select>
          </FormField>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2 border-t border-border">
        <div>
          {step > 0 && (
            <Button variant="ghost" iconLeft={<ChevronLeft size={14} />} onClick={() => setStep(s => s - 1)}>
              Back
            </Button>
          )}
        </div>

        <div>
          {step < STEPS.length - 1 ? (
            <Button
              disabled={!canGoNext}
              iconRight={<ChevronRight size={14} />}
              onClick={() => setStep(s => s + 1)}
            >
              Next
            </Button>
          ) : (
            <Button
              disabled={!brief.trim()}
              onClick={handleGenerate}
              iconLeft={<Sparkles size={14} />}
            >
              Generate Campaign
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Page wrapper ─────────────────────────────────────────────────────────────

export default function AiCampaignWizard() {
  return (
    <FbWorkspaceProvider>
      <>
        <Topbar
          title="AI Campaign Generator"
          breadcrumb="Facebook Ads / AI Campaign"
        />
        <WizardInner />
      </>
    </FbWorkspaceProvider>
  );
}
